/**
 * 统一图标库 —— 纯线性 stroke 风格。
 *
 * 设计规范（与原 ShareIcon 一致）：
 * - viewBox 0 0 24 24
 * - stroke="currentColor"，颜色继承父元素
 * - strokeWidth 1.8，圆角端点（linecap/linejoin round）
 * - fill="none"（除少数填充态：HeartFilled / StarFilled）
 * - 所有图标默认 size=16，可通过 props.size 覆盖
 * - aria-hidden + focusable="false"（装饰性，由父按钮提供 aria-label）
 *
 * 新增图标时请遵循同一规范，保持视觉统一。
 */

export type IconProps = React.SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 16, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** 分享 / 转发（向右上的箭头 + 收尾弧线） */
export function Share(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </Svg>
  );
}

/** 关闭 ✕ */
export function Close(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </Svg>
  );
}

/** 搜索（放大镜） */
export function Search(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </Svg>
  );
}

/** 下载（下箭头 + 底托） */
export function Download(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="12" y1="4" x2="12" y2="16" />
      <polyline points="6 10 12 16 18 10" />
      <line x1="5" y1="20" x2="19" y2="20" />
    </Svg>
  );
}

/** 心形（空心，未点赞） */
export function Heart(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 20s-7-4.5-9-9.5C1.5 6 5 3 8 4.5c1.5.75 2.5 2 4 4 1.5-2 2.5-3.25 4-4 3-1.5 6.5 1.5 5 6-2 5-9 9.5-9 9.5z" />
    </Svg>
  );
}

/** 心形（实心，已点赞） */
export function HeartFilled(props: IconProps) {
  return (
    <Svg fill="currentColor" stroke="none" {...props}>
      <path d="M12 20s-7-4.5-9-9.5C1.5 6 5 3 8 4.5c1.5.75 2.5 2 4 4 1.5-2 2.5-3.25 4-4 3-1.5 6.5 1.5 5 6-2 5-9 9.5-9 9.5z" />
    </Svg>
  );
}

/** 星形（空心，未收藏） */
export function Star(props: IconProps) {
  return (
    <Svg {...props}>
      <polygon points="12 3 14.5 9 21 9.5 16 14 17.5 20.5 12 17 6.5 20.5 8 14 3 9.5 9.5 9" />
    </Svg>
  );
}

/** 星形（实心，已收藏） */
export function StarFilled(props: IconProps) {
  return (
    <Svg fill="currentColor" stroke="none" {...props}>
      <polygon points="12 3 14.5 9 21 9.5 16 14 17.5 20.5 12 17 6.5 20.5 8 14 3 9.5 9.5 9" />
    </Svg>
  );
}

/** 太阳（浅色主题） */
export function Sun(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <line x1="4.9" y1="4.9" x2="7" y2="7" />
      <line x1="17" y1="17" x2="19.1" y2="19.1" />
      <line x1="4.9" y1="19.1" x2="7" y2="17" />
      <line x1="17" y1="7" x2="19.1" y2="4.9" />
    </Svg>
  );
}

/** 月亮（深色主题） */
export function Moon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a7 7 0 1 0 10.5 10.5z" />
    </Svg>
  );
}

/** 地球（语言切换） */
export function Globe(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path d="M12 3c2.5 2.5 4 5.5 4 9s-1.5 6.5-4 9c-2.5-2.5-4-5.5-4-9s1.5-6.5 4-9z" />
    </Svg>
  );
}

/** 菜单 ☰ */
export function Menu(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </Svg>
  );
}

/** 首页（小房子） */
export function Home(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </Svg>
  );
}

/** 勾选 ✓ */
export function Check(props: IconProps) {
  return (
    <Svg {...props}>
      <polyline points="5 12.5 10 17.5 19 6.5" />
    </Svg>
  );
}

/** 加号 ＋ */
export function Plus(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </Svg>
  );
}

/** 图片占位（山 + 太阳的通用 image 图标） */
export function Image(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <polyline points="5 19 10 14 13 17 16 13 21 18" />
    </Svg>
  );
}

/** 眼睛（浏览量） */
export function Eye(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="2.5" />
    </Svg>
  );
}

/** 锁（升级解锁） */
export function Lock(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4.5" y="10" width="15" height="10" rx="2" />
      <path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10" />
      <line x1="12" y1="14" x2="12" y2="17" />
    </Svg>
  );
}

/* ─────────────── 状态类 ─────────────── */

/** 警告三角 ⚠️ */
export function AlertTriangle(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3L2 20h20L12 3z" />
      <line x1="12" y1="9" x2="12" y2="14" />
      <circle cx="12" cy="17.5" r="0.6" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** 成功 ✓ 圆 */
export function CheckCircle(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="7.5 12.5 10.5 15.5 16.5 8.5" />
    </Svg>
  );
}

/** 失败 ✕ 圆 */
export function XCircle(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
      <line x1="15.5" y1="8.5" x2="8.5" y2="15.5" />
    </Svg>
  );
}

/** 沙漏 ⏳⏱️ */
export function Hourglass(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 3h12M6 21h12" />
      <path d="M6 3v3c0 3 3 4 6 6 3-2 6-3 6-6V3" />
      <path d="M6 21v-3c0-3 3-4 6-6 3 2 6 3 6 6v3" />
    </Svg>
  );
}

/** 加载/旋转 🔄 */
export function Loader(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 12a7 7 0 0 1 12-5" />
      <polyline points="17 3 17 7 13 7" />
      <path d="M19 12a7 7 0 0 1-12 5" />
      <polyline points="7 21 7 17 11 17" />
    </Svg>
  );
}

/** 问号圆 ❓ */
export function HelpCircle(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3.5" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** 庆祝 🎉（简化为礼花线条） */
export function PartyPopper(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 21l9-9" />
      <path d="M3 21l5-2 2-5-5 2-2 5z" />
      <line x1="14" y1="3" x2="14" y2="6" />
      <line x1="18" y1="6" x2="20" y2="4" />
      <line x1="17" y1="10" x2="20" y2="11" />
      <line x1="11" y1="4" x2="9" y2="2" />
    </Svg>
  );
}

/* ─────────────── 功能类 ─────────────── */

/** 旗帜 🚩 */
export function Flag(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="5" y1="21" x2="5" y2="4" />
      <path d="M5 4h11l-2 3 2 3H5" />
    </Svg>
  );
}

/** 靶心 🎯 */
export function Target(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** 扳手 🔧 */
export function Wrench(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M15 4a4 4 0 0 0-4 6l-7 7 3 3 7-7a4 4 0 0 0 6-4l-3 3-3-1-1-3 2-3z" />
    </Svg>
  );
}

/** 钻石 💎 */
export function Gem(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 3h12l3 6-9 12L3 9l3-6z" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="3" x2="12" y2="9" />
      <line x1="15" y1="3" x2="12" y2="9" />
      <line x1="12" y1="9" x2="12" y2="21" />
    </Svg>
  );
}

/** 闪电 ⚡ */
export function Zap(props: IconProps) {
  return (
    <Svg {...props}>
      <polyline points="13 3 5 13 11 13 11 21 19 11 13 11 13 3" />
    </Svg>
  );
}

/** 信用卡 💳 */
export function CreditCard(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <line x1="7" y1="15" x2="11" y2="15" />
    </Svg>
  );
}

/** 硬币 🪙 */
export function Coin(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="4" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="20" />
    </Svg>
  );
}

/** 柱状图 📊 */
export function BarChart(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="4" y1="20" x2="20" y2="20" />
      <rect x="5.5" y="11" width="3" height="9" />
      <rect x="10.5" y="7" width="3" height="13" />
      <rect x="15.5" y="13" width="3" height="7" />
    </Svg>
  );
}

/** 减号 − */
export function Minus(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="6" y1="12" x2="18" y2="12" />
    </Svg>
  );
}

/* ─────────────── 管理后台类 ─────────────── */

/** 人群（用户） */
export function Users(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3 2.5-5 6-5s6 2 6 5" />
      <circle cx="16.5" cy="8.5" r="2.5" />
      <path d="M15 15.2c3 0.3 5 2.2 5 4.8" />
    </Svg>
  );
}

/** 文档 / 文章 */
export function FileText(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 3h8l4 4v14H6z" />
      <polyline points="13 3 13 7 17 7" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="15" y2="16" />
    </Svg>
  );
}

/** 垃圾桶 🗑️ */
export function Trash(props: IconProps) {
  return (
    <Svg {...props}>
      <polyline points="4 6 20 6" />
      <path d="M6 6l1 14h10l1-14" />
      <path d="M9 6V4h6v2" />
      <line x1="10" y1="10" x2="10" y2="16" />
      <line x1="14" y1="10" x2="14" y2="16" />
    </Svg>
  );
}

/** 编辑 / 铅笔 ✏️ */
export function Edit(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 20h4l11-11-4-4L4 16v4z" />
      <line x1="13" y1="6" x2="18" y2="11" />
    </Svg>
  );
}

/** 外链 ↗ */
export function ExternalLink(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 4h6v6" />
      <line x1="20" y1="4" x2="10" y2="14" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </Svg>
  );
}

/** 魔法棒 / AI 优化 ✨ */
export function Sparkles(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 3v4M3 5h4M6 17v4M4 19h4" />
      <path d="M13 4l2.5 6.5L22 13l-6.5 2.5L13 22l-2.5-6.5L4 13l6.5-2.5L13 4z" />
    </Svg>
  );
}

/** 骰子 / 随机提示词 🎲 */
export function Dices(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="4" width="16" height="16" rx="3.5" />
      <circle cx="9" cy="9" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="9" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="9" cy="15" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="15" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** 立方体 / 3D 模型 */
export function Cube(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 2 3 7v10l9 5 9-5V7l-9-5z" />
      <path d="M3 7l9 5 9-5" />
      <path d="M12 12v10" />
    </Svg>
  );
}

/** 邮件 / 联系我们（信封） */
export function Mail(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="m3.5 7 7.6 5.6a1.5 1.5 0 0 0 1.8 0L20.5 7" />
    </Svg>
  );
}
